import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import {formatDate} from '../utils/dataConvertor';
import getAuthConfig from "../functions/getAuthConfig";
import DateSearchHeader from "../components/dashboard/DateSearchHeader";
import UpdateButton from "../components/UpdateButton";
import useQueueStatus from "../hooks/useQueueStatus";
import { useQueue } from "../context/QueueContext";
import { API_BASE } from '../config/env';
import { exportToExcel } from '../utils/exportToExcel';
import { getDateRangeForDays } from '../utils/dateRangeUtils';

// Toast is now global (GlobalToast in App.jsx) — no local import needed.

// const MODULE_NAMES = {
//   character: 'चरित्र प्रमाण पत्र',
//   tenant: 'किरायेदार सत्यापन',
//   domestic: 'घरेलू सहायक सत्यापन',
//   employee: 'कर्मचारी सत्यापन',
//   complaint: 'शिकायत निवारण',
//   all: 'सत्यापन',
// };

const MODULE_NAMES = {
  character: 'Character Certificate',
  tenant: 'Tenant Verification',
  domestic: 'Domestic Help Verification',
  employee: 'Employee Verification',
  complaint: 'Complaint Redressal',
  postmortem: 'Postmortem',
  all: 'Verification',
};

// const LOC_HEADINGS = {
//   // Long names
//   totaldcp:     'कुल डीसीपी पर लम्बित',
//   totalliu:     'कुल एलआईयू पर लम्बित',
//   totaldcrb:    'कुल डीसीआरबी पर लम्बित',
//   totalps:      'कुल थानों पर लम्बित',
//   totalremain:  'समस्त लम्बित',
//   totaldiff:    'अन्य थानों से सम्बन्धित लम्बित',
//   OTHER_TO_OWN_PS: 'अन्य थानों से सम्बन्धित लम्बित',
  
//   // Short names
//   all:          'कुल प्राप्त',
//   remain:       'कुल लम्बित',
//   ps:           'थानों पर लम्बित',
//   dcrb:         'डीसीआरबी पर लम्बित',
//   liu:          'एलआईयू पर लम्बित',
//   dcp:          'डीसीपी पर लम्बित',
//   own_to_other: 'अन्य थानों से सम्बन्धित लम्बित',
//   other_to_own: 'अन्य थानों से सम्बन्धित लम्बित',
// };


const LOC_HEADINGS = {

  // Long names

  totaldcp:     'Total Pending at DCP',

  totalliu:     'Total Pending at LIU',

  totaldcrb:    'Total Pending at DCRB',

  totalps:      'Total Pending at Police Stations',

  totalremain:  'Total Pending',

  totaldiff:    'Pending Related to Other Police Stations',

  OTHER_TO_OWN_PS: 'Pending Related to Other Police Stations',

  // Short names

  all:          'Total Received',

  remain:       'Total Pending',

  ps:           'Pending at Police Stations',

  dcrb:         'Pending at DCRB',

  liu:          'Pending at LIU',

  dcp:          'Pending at DCP',

  own_to_other: 'Pending Related to Other Police Stations',

  other_to_own: 'Pending Related to Other Police Stations',

};

const VERIFICATION_TYPES = new Set(['character', 'employee', 'tenant', 'domestic', 'all']);

const LIST_ENDPOINTS = {
  complaint : '/complaintList',
  postmortem : '/postmortemList'
};

const PAGE_SIZES = [10, 20, 50, 100];

const hideStatus = (loc) => ['totaldcp','totalliu','totaldcrb','totalps','totalremain','dcp','liu','dcrb','ps','remain'].includes(loc);
const hidePraAdd = (loc) => ['totaldcp','totalliu','totaldcrb','totalps','totalremain','dcp','liu','dcrb','ps','remain'].includes(loc);
const hidePreAdd = (type) =>['complaint', 'postmortem'].includes(type);

function fmt(d) { return d ? d.split('-').reverse().join('-') : ''; }
function stationName(row) { return row.pre_station_name || row.station_name || ''; }
function presentStatus(row) { return row.pre_Current_Status || row.pre_current_status || ''; }

export default function Characters({
  showSearch,
  setShowSearch,
  sdate,
  setSdate,
  edate,
  setEdate
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const loc = searchParams.get('loc') || '';
  const type = searchParams.get('type') || '';
  const days = searchParams.get('days') || '';
  const aging = searchParams.get('aging') || '';
  const stationParam = searchParams.get('ps') || searchParams.get('station') || '';

  const urlSdate = searchParams.get('sdate');
  const urlEdate = searchParams.get('edate');

  const dayRange = days ? getDateRangeForDays(days) : null;
  const effectiveSdate = dayRange?.sdate || urlSdate || sdate;
  const effectiveEdate = dayRange?.edate || urlEdate || edate;

  useEffect(() => {
    if (effectiveSdate && effectiveSdate !== sdate) {
      setSdate(effectiveSdate);
    }
    if (effectiveEdate && effectiveEdate !== edate) {
      setEdate(effectiveEdate);
    }
  }, [effectiveSdate, effectiveEdate, sdate, edate, setSdate, setEdate]);

  const handleDateSearch = (newSdate, newEdate) => {
    setSdate(newSdate);
    setEdate(newEdate);
    const nextParams = { type, loc, sdate: newSdate, edate: newEdate };
    if (selectedPoliceStation) {
      nextParams.ps = selectedPoliceStation;
    }
    setSearchParams(nextParams);
  };

  const [selectedPoliceStation, setSelectedPoliceStation] = useState(stationParam);

  useEffect(() => {
    setSelectedPoliceStation(stationParam);
  }, [stationParam]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [policeStations, setPoliceStations] = useState([]);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('');      // 'station' | 'request_number' | 'request_date' | 'pre_status'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  const tableRef = useRef(null);

  // ── Real-time queue status (button states: PENDING / PROCESSING) ──────────
  const { queueStatuses } = useQueueStatus();

  // ── Row-update subscription ───────────────────────────────────────────────
  // When queue:completed fires for a request that belongs to this table,
  // patch the row's status columns in-place — no full-table reload needed.
  // Toast is handled globally by GlobalToast in App.jsx.
  const { subscribeCompleted } = useQueue();
  const rowUpdateRef = useRef(null);

  // Always points to the latest setRows closure (updated every render)
  rowUpdateRef.current = (data) => {
    const reqNum      = String(data.request_number || '');
    const preStatus   = data.pre_Current_Status || null;
    const perStatus   = data.per_Current_Status || null;
    const activeStatus = data.active_status || data.current_status || null;

    setRows((prevRows) =>
      prevRows.map((row) => {
        if (String(row.request_number || '') !== reqNum) return row;
        return {
          ...row,
          ...(preStatus    !== null ? { pre_Current_Status: preStatus    } : {}),
          ...(perStatus    !== null ? { per_Current_Status: perStatus    } : {}),
          ...(activeStatus !== null ? { Current_Status:     activeStatus } : {}),
        };
      })
    );
  };

  // Subscribe once on mount — stable wrapper ensures no subscribe/unsubscribe gap
  useEffect(() => {
    const onCompleted = (data) => rowUpdateRef.current?.(data);
    return subscribeCompleted(onCompleted);
  }, []); // intentionally empty — subscribe once for the component lifetime
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Callback from UpdateButton after the HTTP POST completes.
   * Merges any status fields returned immediately by the server.
   */
  const handleRowStatusUpdate = (request_number, apiResponse) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        const rowReqNum = row.request_number;
        if (String(rowReqNum) !== String(request_number)) return row;
        return {
          ...row,
          ...(apiResponse.pre_Current_Status !== undefined
            ? { pre_Current_Status: apiResponse.pre_Current_Status }
            : {}),
          ...(apiResponse.per_Current_Status !== undefined
            ? { per_Current_Status: apiResponse.per_Current_Status }
            : {}),
        };
      })
    );
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try { 
        setLoading(true);
        setError("");
        setRows([]);

        const endpoint = VERIFICATION_TYPES.has(type)
          ? '/verificationList'
          : LIST_ENDPOINTS[type];
        if (!endpoint) {
          throw new Error('Unsupported verification type');
        }
        const res = await axios.get(`${API_BASE}${endpoint}`, {
          params: {
            type,
            loc,
            sdate: effectiveSdate,
            edate: effectiveEdate,
            ps: selectedPoliceStation,
            days,
            aging,
            sortBy,
            sortOrder,
          },
          ...getAuthConfig(),
        });

        if (isCancelled) return;

        const records = Array.isArray(res.data) ? res.data : [];
        const stationEntries = records
          .map((record) => {
            const label = record.pre_station_name || record.station_name;
            const value = record.pre_station_code || record.station_code || label;

            return label ? [value, { value, label }] : null;
          })
          .filter(Boolean);
        const stations = [...new Map(stationEntries).values()];

        setRows(records);
        setCurrentPage(1);
        setPoliceStations((prev) => {
          if (prev.length > 0 && selectedPoliceStation) return prev;
          return stations.length > 0 ? stations : prev;
        });

      } catch (e) {
        if (isCancelled) return;
        setError(e.response?.data?.error || e.message);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [loc, type, effectiveSdate, effectiveEdate, selectedPoliceStation, days, aging, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const activePage = Math.min(currentPage, totalPages);
  const paginatedRows = rows.slice(
    (activePage - 1) * pageSize,
    activePage * pageSize
  );


  const moduleName = MODULE_NAMES[type] || MODULE_NAMES.character;
  const locHeading = LOC_HEADINGS[loc] || '';
  const heading = `${locHeading} ${moduleName}`.trim();
  const hideStatusCol = hideStatus(loc);
  const hidePraAddCol = hidePraAdd(loc);
  const hidePreAddCol = hidePreAdd(type);

  // ── Excel export ───────────────────────────────────────────────────────────
  function handleExcelExport() {
    const isComplaintOrPostmortem = hidePreAdd(type);

    // Build column headers to match exactly what the table renders
    const headers = [
      'Sr.No.',
      'Service',
      'Police Station',
      'Request Number',
      'Request Date',
      'Applicant Name',
      ...(!isComplaintOrPostmortem ? ['Present Address'] : []),
      !isComplaintOrPostmortem ? 'Present Add Status' : 'Status',
      ...(!hidePraAdd(loc) ? ['Permanent Address'] : []),
      ...(!hideStatus(loc) ? ['Permanent Add Status'] : []),
    ];

    const data = rows.map((r, i) => [
      i + 1,
      r.service || (MODULE_NAMES[type] || type).toUpperCase(),
      stationName(r),
      r.request_number,
      formatDate(r.request_date),
      r.applicant_name,
      ...(!isComplaintOrPostmortem ? [r.present_address || ''] : []),
      presentStatus(r),
      ...(!hidePraAdd(loc) ? [r.permanent_address || ''] : []),
      ...(!hideStatus(loc) ? [r.per_Current_Status || ''] : []),
    ]);

    // Format date range string for the subtitle row
    const fmt = (d) => (d ? d.split('-').reverse().join('-') : '');
    const dateRange = (effectiveSdate || effectiveEdate)
      ? `Date From: ${fmt(effectiveSdate) || 'All'}    To: ${fmt(effectiveEdate) || 'All'}`
      : '';

    // File name: "Total Pending Character Certificate" → "Total_Pending_Character_Certificate"
    const safeFileName = heading.replace(/\s+/g, '_') || 'List';

    exportToExcel({
      title: heading,
      dateRange,
      headers,
      data,
      fileName: safeFileName,
      sheetName: heading.slice(0, 31),
    });
  }
  // ─────────────────────────────────────────────────────────────────────────

  // ── Server-side sort ───────────────────────────────────────────────────────
  const SORT_COLS = ['station', 'request_number', 'request_date'];

  function handleSort(col) {
    if (!SORT_COLS.includes(col)) return;
    if (sortBy === col) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  }

  /** Returns the aria-sort value and the visible arrow for a header column */
  function SortArrow({ col }) {
    if (sortBy !== col) {
      return <span className="sort-arrow sort-arrow--idle" aria-hidden="true">⇅</span>;
    }
    return sortOrder === 'asc'
      ? <span className="sort-arrow sort-arrow--asc"  aria-hidden="true">▲</span>
      : <span className="sort-arrow sort-arrow--desc" aria-hidden="true">▼</span>;
  }
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <>
          <Navbar />

    <div style={{ padding: "20px 30px" }}>

      <DateSearchHeader
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        sdate={effectiveSdate}
        setSdate={setSdate}
        edate={effectiveEdate}
        setEdate={setEdate}
        policeStations={policeStations}
        selectedPoliceStation={selectedPoliceStation}
        setSelectedPoliceStation={setSelectedPoliceStation}
        onSearch={handleDateSearch}
      />


      <div className="table-header-title">
        <h3>
          {heading}
        </h3>
        <button
          className="btn-excel-export"
          onClick={handleExcelExport}
          disabled={loading || rows.length === 0}
          title={rows.length === 0 ? 'No data to export' : `Download ${rows.length} records as Excel`}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 18l-2-3h1.2l1.3 2 1.3-2H11.5l-2 3 2.2 3H10.3l-1.3-2-1.3 2H6.3l2.2-3zm5 3v-6h1v5h2v1h-3z"/>
          </svg>
          Excel ({rows.length})
        </button>
      </div>

      <div className="table-wrapper characters-table-wrapper" style={{ padding: '20px' }}>
      

        <table id="printable" ref={tableRef} className="characters-table">
          <colgroup>
            <col className="characters-table__serial-col" />
            <col className="characters-table__station-col" />
            <col className="characters-table__request-col" />
            <col className="characters-table__date-col" />
            <col className="characters-table__applicant-col" />
            {!hidePreAddCol && <col className="characters-table__address-col" />}
            <col className="characters-table__status-col" />
            {!hidePraAddCol && <col className="characters-table__address-col" />}
            {!hideStatusCol && <col className="characters-table__status-col" />}
            {type !== 'postmortem' && <col className="characters-table__action-col" />}
          </colgroup>
          <thead>
            {/* {heading && (
              <tr><th  colSpan= {!hidePraAddCol ? 12 : 9} style={{ textAlign: 'center', fontSize: '18px' }}>{heading}</th></tr>
            )} */}
            <tr>
              <th>Sr.No.</th>
            
              <th
                className={`sortable-th${sortBy === 'station' ? ' sorted' : ''}`}
                onClick={() => handleSort('station')}
                aria-sort={sortBy === 'station' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                POLICE STATION <SortArrow col="station" />
              </th>
              <th
                className={`sortable-th${sortBy === 'request_number' ? ' sorted' : ''}`}
                onClick={() => handleSort('request_number')}
                aria-sort={sortBy === 'request_number' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                REQUEST NUMBER <SortArrow col="request_number" />
              </th>
              <th
                className={`sortable-th${sortBy === 'request_date' ? ' sorted' : ''}`}
                onClick={() => handleSort('request_date')}
                aria-sort={sortBy === 'request_date' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                REQUEST DATE <SortArrow col="request_date" />
              </th>
              <th>APPLICANT NAME</th>
              {!hidePreAddCol && <th>PRESENT ADDRESS</th>}
              {!hidePreAddCol ? <th>PRESENT<br/>ADD<br/>STATUS</th> : <th>STATUS</th>}
              {!hidePraAddCol && <th>PERMANENT ADDRESS</th>}
              {!hideStatusCol && <th>PERMANENT <br/>ADD<br/> STATUS</th>}
              {type !== 'postmortem' && <th>Status Update</th>}
            </tr> 
          </thead>
          <tbody id="pending_Details">
            {loading && (
              <tr><td colSpan={!hidePraAddCol ? 13 : 10} style={{ textAlign: 'center', padding: '20px' }}>लोड हो रहा है...</td></tr>
            )}
            {error && (
              <tr><td colSpan={!hidePraAddCol ? 13 : 10} style={{ textAlign: 'center', color: 'red' }}>{error}</td></tr>
            )}
            {!loading && !error && paginatedRows.length === 0 && (
              <tr>
                <td colSpan={!hidePraAddCol ? 13 : 10} style={{ textAlign: 'center', padding: '20px' }}>
                  No matching records found.
                </td>
              </tr>
            )}
            {!loading && paginatedRows.map((r, i) => {
              const reqNum = r.request_number;
              const isInQueue = !!queueStatuses[String(reqNum)];
              return (
                <tr key={i} className={isInQueue ? 'row--in-queue' : ''}>
                  <td>{((activePage - 1) * pageSize) + i + 1}</td>
                  <td>{stationName(r)}</td>
                  <td>{reqNum}</td>
                  <td>{formatDate(r.request_date)}</td>
                  <td className="characters-table__applicant">{r.applicant_name}</td>
                  {!hidePreAddCol && <td className="characters-table__address">{r.present_address}</td>}
                  <td>{presentStatus(r)}</td>
                  {!hidePraAddCol && <td className="characters-table__address">{r.permanent_address}</td>}
                  {!hideStatusCol && (
                    <td>{r.per_Current_Status}</td>
                  )}
                  

                 {type !== 'postmortem' && <td>
                    <UpdateButton
                      type={r.service ? r.service.toLowerCase() : type}
                      request_number={reqNum}
                      queueStatus={queueStatuses[String(reqNum)]}
                      onStatusUpdate={handleRowStatusUpdate}
                    />
                  </td>}

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <nav className="list-pagination" aria-label="List pagination">
        <span>{rows.length} record{rows.length === 1 ? '' : 's'}</span>
        <label>
          Rows per page
          <select
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              setCurrentPage(1);
            }}
          >
            {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => setCurrentPage(activePage - 1)} disabled={activePage === 1}>
          Previous
        </button>
        <span>Page {activePage} of {totalPages}</span>
        <button type="button" onClick={() => setCurrentPage(activePage + 1)} disabled={activePage === totalPages}>
          Next
        </button>
      </nav>
    </div>
    </>
  );
}
